export const IMAGE_EDIT_WORK_MODE = {
  GENERATIVE: "generative",
  LAYERS: "layers",
};

export const LAYERS_NORMALIZED_MAX = 1000;
export const LAYERS_UNDO_LIMIT = 50;

export const cloneLayers = (layers = []) =>
  layers.map((layer) => ({ ...layer }));

export const isSameLayerBox = (layer, box) =>
  Math.abs((Number(layer?.x) || 0) - (Number(box?.x) || 0)) < 0.001 &&
  Math.abs((Number(layer?.y) || 0) - (Number(box?.y) || 0)) < 0.001 &&
  Math.abs((Number(layer?.width) || 0) - (Number(box?.width) || 0)) < 0.001 &&
  Math.abs((Number(layer?.height) || 0) - (Number(box?.height) || 0)) < 0.001;

export const areLayerListsEqual = (left = [], right = []) =>
  left.length === right.length &&
  left.every(
    (layer, index) =>
      layer?.id === right[index]?.id &&
      (Number(layer?.zIndex) || 0) === (Number(right[index]?.zIndex) || 0) &&
      (layer?.name || "") === (right[index]?.name || "") &&
      isSameLayerBox(layer, right[index])
  );

const LAYER_KIND_LOGO_PATTERN = /\blogo\b/i;
const LAYER_KIND_TEXT_PATTERN =
  /\b(text|texto|headline|title|t[íi]tulo|copy|caption|cta|llamado)\b/i;

export const LAYER_KIND = {
  IMAGE: "image",
  OBJECT: "object",
  TEXT: "text",
  LOGO: "logo",
};

export const getLayerKind = (layer = {}) => {
  if (layer.isBackground) return LAYER_KIND.IMAGE;
  const haystack = `${layer.name || ""} ${layer.description || ""}`;
  if (LAYER_KIND_LOGO_PATTERN.test(haystack)) return LAYER_KIND.LOGO;
  if (LAYER_KIND_TEXT_PATTERN.test(haystack)) return LAYER_KIND.TEXT;
  if (String(layer.id || "").startsWith("imported_")) return LAYER_KIND.IMAGE;
  return LAYER_KIND.OBJECT;
};

/**
 * Maps sidebar display order (front to back, top of list first) to zIndex.
 * The first displayed layer is painted last (highest zIndex).
 */
export const applyDisplayOrderToZIndex = (
  layers = [],
  orderedDisplayIds = []
) => {
  const byId = new Map(layers.map((layer) => [layer.id, layer]));
  const ordered = [];
  const seen = new Set();

  for (const id of orderedDisplayIds) {
    const layer = byId.get(id);
    if (!layer || seen.has(id)) continue;
    ordered.push(layer);
    seen.add(id);
  }

  for (const layer of layers) {
    if (!seen.has(layer.id)) {
      ordered.push(layer);
    }
  }

  const lastIndex = ordered.length - 1;
  return ordered
    .map((layer, displayIndex) => ({
      ...layer,
      zIndex: lastIndex - displayIndex,
    }))
    .sort((left, right) => (Number(left.zIndex) || 0) - (Number(right.zIndex) || 0));
};

export const layersRecordToArray = (layers = {}) =>
  Object.values(layers || {}).sort(
    (left, right) => (left.zIndex || 0) - (right.zIndex || 0)
  );

export const layersArrayToRecord = (layers = []) =>
  layers.reduce((record, layer) => {
    if (!layer?.id) return record;
    record[layer.id] = layer;
    return record;
  }, {});

export const normalizedToPixels = ({
  x,
  y,
  width,
  height,
  displayWidth,
  displayHeight,
}) => ({
  x: ((Number(x) || 0) / LAYERS_NORMALIZED_MAX) * displayWidth,
  y: ((Number(y) || 0) / LAYERS_NORMALIZED_MAX) * displayHeight,
  width: ((Number(width) || 0) / LAYERS_NORMALIZED_MAX) * displayWidth,
  height: ((Number(height) || 0) / LAYERS_NORMALIZED_MAX) * displayHeight,
});

export const pixelsToNormalized = ({
  x,
  y,
  width,
  height,
  displayWidth,
  displayHeight,
}) => {
  const clamp = (value) =>
    Math.min(Math.max(value, 0), LAYERS_NORMALIZED_MAX);

  return {
    x: clamp((Number(x) / displayWidth) * LAYERS_NORMALIZED_MAX),
    y: clamp((Number(y) / displayHeight) * LAYERS_NORMALIZED_MAX),
    width: clamp((Number(width) / displayWidth) * LAYERS_NORMALIZED_MAX),
    height: clamp((Number(height) / displayHeight) * LAYERS_NORMALIZED_MAX),
  };
};

const isLocalObjectUrl = (url = "") =>
  url.startsWith("blob:") || url.startsWith("data:");

const loadImageElement = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Failed to load image"));
    image.src = url;
  });

/** Loads an image drawable on canvas. Remote URLs are fetched as blobs to avoid a non-CORS cached `<img>` response. */
export const loadHtmlImage = async (url) => {
  if (!url) {
    throw new Error("Missing image URL");
  }

  if (isLocalObjectUrl(url)) {
    return loadImageElement(url);
  }

  const response = await fetch(url, { mode: "cors", cache: "no-store" });
  if (!response.ok) {
    throw new Error(`Failed to load image (${response.status})`);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    return await loadImageElement(objectUrl);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};
